// ============================================================================
// FORM 1120 MAPPINGS
// ============================================================================
// This file contains the predefined groupings for Form 1120 and determines
// which groupings allow SubGroupings.

export const DEFAULT_FS_REF = "Ungrouped";
export const DEFAULT_GROUPING = "Ungrouped";
export const DEFAULT_SUBGROUPING = "";

// FS Ref predefined list (the 4-tier hierarchy Level 2)
export const FS_REF_LIST = [
  "Assets",
  "Liabilities",
  "Equity",
  "Income",
  "Deductions",
  "Ungrouped"
] as const;

export type FSRefType = typeof FS_REF_LIST[number];

// Form 1120 Groupings by FS Ref
export const FORM_1120_GROUPINGS: Record<string, string[]> = {
  Assets: [
    "Cash",
    "Trade notes and accounts receivable",
    "Inventories",
    "U.S. government obligations",
    "Tax-exempt securities",
    "Other current assets",
    "Loans to shareholders",
    "Mortgage and real estate loans",
    "Other investments",
    "Buildings and other depreciable assets",
    "Depletable assets",
    "Land",
    "Intangible assets",
    "Other assets"
  ],
  Liabilities: [
    "Accounts payable",
    "Mortgages, notes, bonds payable in less than 1 year",
    "Other current liabilities",
    "Loans from shareholders",
    "Mortgages, notes, bonds payable in 1 year or more",
    "Other liabilities"
  ],
  Equity: [
    "Preferred stock",
    "Common stock",
    "Additional paid-in capital",
    "Retained earnings-Appropriated",
    "Retained earnings-Unappropriated",
    "Adjustments to shareholders equity",
    "Treasury Stock"
  ],
  Income: [
    "Gross receipts or sales",
    "COGS - Inventory BOY",
    "COGS - Purchases",
    "COGS - Cost of Labor",
    "COGS - Additional 263A costs",
    "COGS - Other Costs",
    "COGS - Inventory EOY",
    "Dividends",
    "Interest Income",
    "Gross rents",
    "Gross royalties",
    "Capital gain net income",
    "Net gain or (loss) Form 4797",
    "Other income"
  ],
  Deductions: [
    "Compensation of officers",
    "Salaries and wages",
    "Repairs and maintenance",
    "Bad debts",
    "Rents",
    "Taxes and licenses",
    "Interest",
    "Charitable contributions",
    "Depreciation",
    "Depletion",
    "Advertising",
    "Pension, profit-sharing, etc.",
    "Employee benefit programs",
    "Other deductions"
  ],
  Ungrouped: ["Ungrouped"]
};

// Groupings that allow SubGroupings for Form 1120
export const SUBGROUPING_ALLOWED: string[] = [
  "Other income",
  "Other deductions",
  "Other income (loss)",
  "Other current assets",
  "Other investments",
  "Other assets",
  "Other current liabilities",
  "Other liabilities",
  "Retained earnings-Appropriated",
  "Adjustments to shareholders equity",
  "Ungrouped"
];

// ============================================================================
// BUILD REVERSE LOOKUP MAP (Grouping -> FS Ref)
// ============================================================================

/**
 * Build a reverse lookup map from Grouping to FS Ref
 * This is built once at module load for performance
 */
function buildGroupingToFSRefMap(): Map<string, string> {
  const map = new Map<string, string>();
  
  for (const [fsRef, groupings] of Object.entries(FORM_1120_GROUPINGS)) {
    for (const grouping of groupings) {
      map.set(grouping, fsRef);
    }
  }
  
  return map;
}

// Pre-built lookup map for O(1) FS Ref lookups
const GROUPING_TO_FS_REF_MAP = buildGroupingToFSRefMap();

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a grouping allows SubGroupings
 */
export function allowsSubGrouping(grouping: string): boolean {
  return SUBGROUPING_ALLOWED.includes(grouping);
}

/**
 * Get all available groupings as a flat list for dropdown
 */
export function getAllGroupings(): string[] {
  return Object.values(FORM_1120_GROUPINGS).flat();
}

/**
 * Get groupings by FS Ref category
 */
export function getGroupingsByFSRef(fsRef: string): string[] {
  return FORM_1120_GROUPINGS[fsRef] || [];
}

/**
 * Get the FS Ref for a given Grouping
 * This is the key helper for the 4-tier hierarchy (Entity → FS Ref → Grouping → SubGrouping)
 * 
 * @param grouping - The grouping name (e.g., "Cash", "Accounts payable")
 * @returns The FS Ref category (e.g., "Assets", "Liabilities") or "Ungrouped" if not found
 */
export function getFSRef(grouping: string): string {
  if (!grouping) {
    return DEFAULT_FS_REF;
  }
  
  return GROUPING_TO_FS_REF_MAP.get(grouping) || DEFAULT_FS_REF;
}

/**
 * Get all unique FS Refs from a list of accounts
 * Useful for building hierarchy when only some FS Refs have accounts
 */
export function getUniqueFSRefsFromGroupings(groupings: string[]): string[] {
  const fsRefs = new Set<string>();
  
  for (const grouping of groupings) {
    fsRefs.add(getFSRef(grouping));
  }
  
  // Sort in the predefined order
  return FS_REF_LIST.filter(fsRef => fsRefs.has(fsRef));
}

/**
 * Validate that a grouping exists in the system
 */
export function isValidGrouping(grouping: string): boolean {
  return GROUPING_TO_FS_REF_MAP.has(grouping);
}

/**
 * Get the display order for FS Refs (for consistent sorting)
 */
export function getFSRefSortOrder(fsRef: string): number {
  const index = FS_REF_LIST.indexOf(fsRef as FSRefType);
  return index === -1 ? FS_REF_LIST.length : index;
}
